import { Injectable, signal, computed, effect, inject, Injector } from '@angular/core';
import { Observable } from 'rxjs';
import { Advertiser, GrandCompte } from '../models/user.model';

@Injectable({ providedIn: 'root' })
export class GrandCompteService {
    private readonly injector = inject(Injector);
    private readonly currentGcSubject = signal<GrandCompte | null>(this.getStoredGrandCompte());

    readonly currentGc = computed(() => this.currentGcSubject());
    readonly currentGrandCompteId = computed(() => this.currentGcSubject()?.id ?? null);
    readonly currentGrandCompte = computed(() => this.currentGcSubject());

    readonly currentGc$ = new Observable<GrandCompte | null>((subscriber) => {
        subscriber.next(this.currentGcSubject());

        const cleanup = effect(
            () => {
                const value = this.currentGcSubject();
                subscriber.next(value);
            },
            { injector: this.injector },
        );

        return () => {
            cleanup.destroy();
        };
    });

    setCurrentGrandCompte(gcId: number): void {
        const grandComptes = JSON.parse(localStorage.getItem('userGrandComptes') || '[]');
        const gc = grandComptes.find((g: GrandCompte) => g.id === gcId);

        if (gc) {
            this.currentGcSubject.set(gc);
            localStorage.setItem('currentGrandCompte', JSON.stringify(gc));
        } else {
            console.error('Grand Compte not found:', gcId);
        }
    }

    updateAdvertiserIndustry(clientId: string, industryId: number): void {
        const grandComptes: GrandCompte[] = JSON.parse(localStorage.getItem('userGrandComptes') || '[]');
        grandComptes.forEach((gc) => {
            const adv = gc.advertisers?.find((a) => a.client_id === clientId);
            if (adv) adv.industry_id = industryId;
        });
        localStorage.setItem('userGrandComptes', JSON.stringify(grandComptes));

        const current = this.currentGcSubject();
        if (current) {
            const adv = current.advertisers?.find((a) => a.client_id === clientId);
            if (adv) {
                const updated: GrandCompte = {
                    ...current,
                    advertisers: current.advertisers.map((a) => (a.client_id === clientId ? { ...a, industry_id: industryId } : a)),
                };
                this.currentGcSubject.set(updated);
                localStorage.setItem('currentGrandCompte', JSON.stringify(updated));
            }
        }
    }

    updateAgencyClientInfo(clientId: string, updates: Partial<Pick<Advertiser, 'advertiser_name' | 'industry_id' | 'logo' | 'active'>>): void {
        const grandComptes: GrandCompte[] = JSON.parse(localStorage.getItem('userGrandComptes') || '[]');
        grandComptes.forEach((gc) => {
            const client = gc.agency?.clients?.find((c) => c.client_id === clientId);
            if (client) Object.assign(client, updates);
        });
        localStorage.setItem('userGrandComptes', JSON.stringify(grandComptes));

        const current = this.currentGcSubject();
        if (current?.agency) {
            const updatedClients = current.agency.clients.map((c) => (c.client_id === clientId ? { ...c, ...updates } : c));
            const updated: GrandCompte = { ...current, agency: { ...current.agency, clients: updatedClients } };
            this.currentGcSubject.set(updated);
            localStorage.setItem('currentGrandCompte', JSON.stringify(updated));
        }
    }

    updateGcAdvertisersLeverGeo(gcId: number, leviers: Record<string, boolean | null>, geolocs: Record<string, string[]>): void {
        const grandComptes: GrandCompte[] = JSON.parse(localStorage.getItem('userGrandComptes') || '[]');
        grandComptes.forEach((gc) => {
            if (gc.id !== gcId) return;
            gc.allowed_leviers = leviers;
            gc.allowed_geolocs = geolocs;
        });
        localStorage.setItem('userGrandComptes', JSON.stringify(grandComptes));

        const current = this.currentGcSubject();
        if (current?.id === gcId) {
            const updated: GrandCompte = { ...current, allowed_leviers: leviers, allowed_geolocs: geolocs };
            this.currentGcSubject.set(updated);
            localStorage.setItem('currentGrandCompte', JSON.stringify(updated));
        }
    }

    clearCurrentGrandCompte(): void {
        this.currentGcSubject.set(null);
        localStorage.removeItem('currentGrandCompte');
        localStorage.removeItem('userGrandComptes');
    }

    private getStoredGrandCompte(): GrandCompte | null {
        try {
            const stored = localStorage.getItem('currentGrandCompte');
            return stored ? JSON.parse(stored) : null;
        } catch (error) {
            console.error('Error parsing stored grand compte:', error);
            return null;
        }
    }
}
